import { useEffect } from "react";
import DataTable, { Column } from "../../components/DataTable";
import Tag from "../../components/Tags";
import { FaAngleDown } from "react-icons/fa";
import PaginationBar from "../../components/PaginationBar";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../store";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { Admission } from "../../types/admissionType";
import {
  fetchAdmissions,
  setFilters,
  setPage,
  setSize,
} from "../../features/admission/admissionSlice";
import {
  exportToExcel,
  popupNavigation,
} from "../../util/functions/UniversalFunctions";
import DownloadButton from "../../components/common/DownloadButton";
import { useBranchCode } from "../../util/hooks/useBranch";

export default function AdmissionList() {
  const navigate = useNavigate();

  const dispatch = useDispatch<AppDispatch>();

  const { data, loading, page, size, totalElements, filters } = useSelector(
    (state: RootState) => state.admissions,
  );

  const branchCode = useBranchCode();

  useEffect(() => {
    dispatch(fetchAdmissions({ page, size, branchCode }));
  }, [dispatch, page, size, branchCode]);

  const handleViewProfile = (id: number) => {
    const staff = data?.find((s) => s.id === id);
    if (staff) {
      navigate(`/admissions/admission-details/${id}`);
    }
  };

  const handleSearchChange = (accessor: string, value: string) => {
    dispatch(setFilters({ [accessor]: value }));
    dispatch(setPage(0));
    dispatch(
      fetchAdmissions({
        page: 0,
        size,
        branchCode,
        filters: { ...filters, [accessor]: value },
      }),
    );
  };

  const columns: Column<Admission>[] = [
    {
      label: "SL NO.",
      accessor: "id",
      searchable: false,
    },
    {
      label: "Profile",
      accessor: "profile",
      searchable: false,
      render: (item) => (
        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-600 text-white font-semibold">
          {item.studentName.charAt(0).toUpperCase()}
        </div>
      ),
    },
    {
      label: "Student Name",
      accessor: "studentName",
      searchable: true,
      render: (item) => (
        <div className="text-neutral-400 text-sm font-bold">
          {item.studentName}
        </div>
      ),
    },
    {
      label: "DOB",
      accessor: "dateOfBirth",
      render: (item) => (
        <div>
          <div className="text-[13px]">{item.dateOfBirth || "---"}</div>
          {/* <span>{item.placeOfBirth || "---"}</span> */}
        </div>
      ),
    },
    {
      label: "Parent Details",
      accessor: "parentName",
      searchable: true,
      render: (item) => (
        <div className="text-start text-[13px]">
          <div className="text-neutral-400 text-nowrap">
            <span className="font-semibold">Mother : </span>{" "}
            {item.motherName || "---"}
          </div>
          <div className=" text-neutral-400">{item.motherPhone || "---"}</div>
          <div className="text-neutral-400 text-nowrap">
            <span className="font-semibold">Father : </span>{" "}
            {item.fatherName || "---"}
          </div>
          <div className=" text-neutral-400">{item.fatherPhone || "----"}</div>
        </div>
      ),
    },
    {
      label: "Enquired For",
      accessor: "academicCycle",
      render: (item) => (
        <div className="w-full">
          <Tag color="primary" text={item.academicCycle} />
        </div>
      ),
    },
    {
      label: "Class",
      accessor: "class",
      render: (item) => {
        const isFinalStatus =
          item.status === "ADMISSION_OFFERED" ||
          item.status === "ADMISSION_REJECTED";

        const displayText =
          item.status === "ADMISSION_OFFERED"
            ? item.className || "Admission Offered"
            : item.status === "ADMISSION_REJECTED"
              ? "Rejected"
              : item.status || "Move to";

        return (
          <button
            disabled={isFinalStatus}
            onClick={() => {
              if (!isFinalStatus) {
                navigate(popupNavigation("admissionStatus"), {
                  state: { admissionId: item.id },
                });
              }
            }}
            className={`px-2 py-1 w-fit flex gap-2 items-center text-[11px] font-medium rounded-[4px]
        ${
          isFinalStatus
            ? "bg-success-50 text-success-600 font-bold capitalize cursor-not-allowed"
            : "bg-primary-50 text-primary-600"
        }`}
          >
            {displayText}
            {!isFinalStatus && <FaAngleDown />}
          </button>
        );
      },
    },
    {
      label: "Action",
      accessor: "action",
      searchable: false,
      render: (item) => (
        <Tag
          text="View Admission"
          onClick={() => handleViewProfile(item.id)}
          color="warning"
          size="sm"
        />
      ),
    },
  ];

  const handleDownload = () => {
    if (!data.length) return;

    exportToExcel(data, "Group_List");
  };

  return (
    <div className="flex flex-col gap-2 ">
      <div className="flex gap-3 items-center justify-end">
        <PaginationBar
          currentPage={page}
          pageSize={size}
          totalItems={totalElements}
          onPageChange={(newPage) => {
            dispatch(setPage(newPage));
          }}
          onPageSizeChange={(newSize) => {
            dispatch(setSize(newSize));
            dispatch(setPage(0));
          }}
        />
        <DownloadButton
          onClick={handleDownload}
          disabled={loading || data.length === 0}
        />
      </div>
      <div className="grid gap-7 bg-white px-6 py-5 rounded-lg">
        <DataTable
          columns={columns}
          data={data}
          onSearchChange={handleSearchChange}
          isLoading={loading}
        />
      </div>
    </div>
  );
}
